import {
    CreateFleetOffer,
    CreateFleetOfferVariables,
} from "_graphql/mutation/__generated__/CreateFleetOffer";
import { Modal, OfficeButton } from "components";
import { createFleetOffer } from "_graphql/visal/mutations/offers";
import { useMutation, useReactiveVar } from "@apollo/client";

import { OfferForm } from "components/forms";
import CreateSlipWizardContainer from "components/forms/slip-wizard/create";
import { currentUserVar } from "_graphql/cache/auth";
import { getDomain } from "router/filter";
import pluralize from "pluralize";
import toast from "react-hot-toast";
import { useFormik } from "formik";
import { useState } from "react";
import { wrapClick } from "utils";
import {
    createDetailsInitialValues,
    validationSchema,
    validationSchemaKEK,
} from "components/forms/slip-wizard/validation";

export {
    validationSchema,
    validationSchemaKEK,
} from "components/forms/slip-wizard/validation";

export default function CreateOfferContainer({
    open,
    setOpen,
    refetch,
}: {
    open: boolean;
    setOpen: (val: boolean) => void;
    refetch?: () => void;
}) {
    const [fleetMode, setFleetMode] = useState(false);
    const [fleetItems, setFleetitems] = useState<any[]>([]);
    const user = useReactiveVar(currentUserVar);
    const domain = getDomain();

    const [createFleet, { loading: creatingFleet }] = useMutation<
        CreateFleetOffer,
        CreateFleetOfferVariables
    >(createFleetOffer, {
        refetchQueries: ["Offers"],
    });

    const fleetForm = useFormik<any>({
        initialValues: createDetailsInitialValues,
        validationSchema:
            domain === "KEK" ? validationSchemaKEK : validationSchema,
        onSubmit: async (variables) => {
            const items =
                fleetItems.length > 0 ? fleetItems : [variables];

            await createFleet({
                variables: {
                    offerInput: items.map((item) => ({
                        brokerage: parseFloat(item.brokerage),
                        insurer_id: item.insurer_id,
                        premium: parseFloat(item.premium),
                        policy_number: item.policy_number,
                        offer_details: JSON.stringify(item.offer_details),
                        class_of_business_id:
                            item.class_of_business_id?.class_of_business_id,
                        employee_id: user?.employee?.employee_id ?? "",
                        rate: parseFloat(item.rate),
                        commission: parseFloat(item.commission),
                        facultative_offer: parseFloat(item.facultative_offer),
                        sum_insured: parseFloat(item.sum_insured),
                        insured_by: item.insured_by,
                        currency: item.currency,
                        risk_location: item.risk_location,
                        offer_comment: item.comment,
                        period_of_insurance_from:
                            item.period_of_insurance_from,
                        period_of_insurance_to: item.period_of_insurance_to,
                    })),
                },
            }).then(({ data }) => {
                if (data?.createFleetOffer) {
                    toast.success("Offer Created Successfully");
                    refetch?.();
                    fleetForm.resetForm();
                    setFleetitems([]);
                    setFleetMode(false);
                } else {
                    toast.error("Failed to create Offer");
                }
            });
        },
        onReset: () => {
            setOpen(false);
            setFleetMode(false);
            setFleetitems([]);
        },
    });

    const handleFleetDetected = (values: any) => {
        fleetForm.setValues(values);
        setFleetMode(true);
    };

    const handleNextFleetItem = async () => {
        const errors = await fleetForm.validateForm();
        if (Object.keys(errors).length > 0) {
            toast.error("Please fill in all required fields");
            return;
        }
        setFleetitems([...fleetItems, fleetForm.values]);
        toast.success(
            "Fleet Item Added, Modify the details and click create offer or add another fleet item",
        );
    };

    const handleModalClose = (val: boolean) => {
        if (!val) {
            setFleetMode(false);
            setFleetitems([]);
        }
        setOpen(val);
    };

    return (
        <Modal
            open={open}
            setOpen={handleModalClose}
            size="4xl"
            hideActions={!fleetMode}
            title="Create New Offer"
            description="Provide the details to add a new offer"
            renderActions={() => (
                <>
                    <OfficeButton
                        type="button"
                        size="sm"
                        disabled={creatingFleet}
                        onClick={wrapClick(fleetForm.handleSubmit)}>
                        {creatingFleet
                            ? "Creating Offer..."
                            : "Create " +
                              (fleetItems?.length > 0
                                  ? pluralize("Offer", fleetItems?.length, true)
                                  : "Offer")}
                    </OfficeButton>
                    {fleetForm?.values?.class_of_business_id?.business_name?.includes(
                        "Fleet",
                    ) && (
                        <div className="flex items-center">
                            <OfficeButton
                                size="sm"
                                variant="secondary"
                                onClick={handleNextFleetItem}>
                                Add Next Fleet Item
                            </OfficeButton>
                        </div>
                    )}
                </>
            )}>
            {fleetMode ? (
                <OfferForm form={fleetForm} />
            ) : (
                <CreateSlipWizardContainer
                    setClose={handleModalClose}
                    refetch={refetch}
                    onFleetDetected={handleFleetDetected}
                />
            )}
        </Modal>
    );
}
